import { Injectable } from '@angular/core';
import { TenantResponseDto, TenantService } from '@core/api';
import { BehaviorSubject, firstValueFrom, map } from 'rxjs';
import { UserStateService } from '../user-state/user-state.service';

@Injectable({
  providedIn: 'root'
})
export class TenantStateService {
  private tenantSubject: BehaviorSubject<TenantResponseDto | null>;

  constructor(
    private tenantService: TenantService,
    private userStateService: UserStateService
  ) {
    this.tenantSubject = new BehaviorSubject<TenantResponseDto | null>(null);
  }

  get tenant(): BehaviorSubject<TenantResponseDto | null> {
    return this.tenantSubject;
  }

  getTenant(tenantId: string) {
    return this.tenantService.v1TenantIdGet(tenantId).pipe(
      map(tenant => {
        this.setTenantSubject(tenant);
        return tenant;
      })
    );
  }

  async init() {
    const userTenantId = this.userStateService.getTenantId();
    if (userTenantId) {
      try {
        const tenant = await firstValueFrom(this.getTenant(userTenantId));
        this.tenant.next(tenant);
      } catch (error) {
        console.error('Error fetching tenant data', error);
      }
    }
  }

  async resolveTenant(tenantId: string): Promise<TenantResponseDto | null> {
    const tenant = this.getTenantState();
    if (tenant && tenant.id === tenantId) {
      return tenant;
    } else {
      try {
        const fetchedTenant = await firstValueFrom(this.getTenant(tenantId));
        this.tenant.next(fetchedTenant);
        return fetchedTenant;
      } catch (error) {
        console.error('Error fetching tenant data', error);
        throw error;
      }
    }
  }

  setTenantSubject(tenantResponseDto: TenantResponseDto | null): void {
    this.tenantSubject.next(tenantResponseDto);
  }

  private getTenantState(): TenantResponseDto | null {
    return this.tenant.value;
  }

  getTenantId(): string {
    const tenantId = this.tenant.value?.id;
    if (tenantId) return tenantId;
    else throw Error('Error while getting tenantId');
  }

  logout(): void {
    this.tenantSubject.next(null);
  }
}
